import React, { Suspense, lazy } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LeftSidebar, MinimalLayout } from "../layout-blueprints";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import PrivateRoute from "./PrivateRoute";
import SuspenseLoading from "../components/Loader";
import ViewManageCustomNotification from "../pages/ManageCustomNotification/ViewCustomNotification";

// Auth pages
const LandingPage = lazy(() => import("../pages/Auth/Landing"));
const LoginPage = lazy(() => import("../pages/Auth/Login"));
const RegisterClient = lazy(() => import("../pages/Auth/RegisterClient"));

// Error pages
// const PageError404 = lazy(() => import("../pages/Error/PageError404"));

// Dashboards
const AdminDashboard = lazy(() => import("../pages/Admin/Dashboard"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const A4Dashboard = lazy(() => import("../pages/Dashboard/A4Dashboard"));

// Shipment List
const AdminShipmentList = lazy(() => import("../pages/Admin/Shipment/List"));
const AdminPrintShipmentList = lazy(() => import("../pages/Admin/Shipment/Print"));
const RVPOrderList = lazy(() => import("../pages/Admin/Shipment/RVPOrderList"));
const DeliveredOrderList = lazy(() =>
  import("../pages/Admin/Shipment/DeliveredOrderList")
);
const RTSOrderList = lazy(() => import("../pages/Admin/Shipment/RTSOrderList"));
const UnDeliveredOrderList = lazy(() =>
  import("../pages/Admin/Shipment/UnDeliveredOrderList")
);
const TPLRemittedOrderList = lazy(() =>
  import("../pages/Admin/Shipment/TPLRemittedOrderList")
);

const ClientWiseDailyPickupReport = lazy(() =>
  import("../pages/Reports/DailyPickup/ClientWise")
);
const OutForDelivery = lazy(() => import("../pages/Reports/OutForDelivery"));

// Client Pages
const ClientShipingList = lazy(() => import("../pages/Client/ClientShiping"));
const ShipmentUpload = lazy(() => import("../pages/Client/ShipmentUpload"));
const SingleShipmentUpload = lazy(() => import("../pages/Client/ShipmentUpload/SingleShipmentUpload"));
const BulkShipmentUpload = lazy(() => import("../pages/Client/ShipmentUpload/BulkShipmentUpload"));
const InboundList = lazy(() => import("../pages/Client/ProductInventory/Inbound/List"));
const ViewInbound = lazy(() => import("../pages/Client/ProductInventory/Inbound/View"));
const InboundUpload = lazy(() => import("../pages/Client/ProductInventory/Inbound/BulkForm"));
const ProductSKUList = lazy(() => import("../pages/Client/ProductInventory/ProductSKU/List"));
const ProductSKUUpload = lazy(() => import("../pages/Client/ProductInventory/ProductSKU/BulkForm"));
const AddProductSKU = lazy(() => import("../pages/Client/ProductInventory/ProductSKU/Form"));

// Scan pages
const PickupScan = lazy(() => import("../pages/Scan/PickupScan"));
const InScan = lazy(() => import("../pages/Scan/InScan"));
const OutScan = lazy(() => import("../pages/Scan/OutScan"));
const RTHScan = lazy(() => import("../pages/Scan/RTHScan"));
const RTHInScan = lazy(() => import("../pages/Scan/RTHInScan"));
const RTHOutScan = lazy(() => import("../pages/Scan/RTHOutScan"));
const RTOHubScan = lazy(() => import("../pages/Scan/RTOHubScan"));
const RTSScan = lazy(() => import("../pages/Scan/RTSScan"));
const RVPInScan = lazy(() => import("../pages/Scan/RVPInScan"));
const RVPOutScan = lazy(() => import("../pages/Scan/RVPOutScan"));
const CargoInScan = lazy(() => import("../pages/Scan/CargoInScan"));
const CargoOutScan = lazy(() => import("../pages/Scan/CargoOutScan"));
const CargoDestInScan = lazy(() => import("../pages/Scan/CargoDestInScan"));
const PickupRejectScan = lazy(() => import("../pages/Scan/PickupReject"));
const PickupCancelledScan = lazy(() => import("../pages/Scan/PickupCancelled"));
const ArrangedForDelivery = lazy(() => import("../pages/Scan/ArrangedForDelivery"));
const DepartedFromAirport = lazy(() => import("../pages/Scan/DepartedFromAirport"));
const FlightDelayed = lazy(() => import("../pages/Scan/FlightDelayed"));
const DeliveryCancelled = lazy(() => import("../pages/Scan/DeliveryCancelled"));
const DeliveryRejected = lazy(() => import("../pages/Scan/DeliveryRejected"));
const ArrivedAtAirportScan = lazy(() => import("../pages/Scan/ArrivedAtAirport"));

// const ScanUndelivery = lazy(() => import("../pages/Admin/Shipment/ScanUndelivery"));
const ManageServiceType = lazy(() => import("../pages/Admin/Setup/ManageServiceType"));
const ViewManageServiceType = lazy(() =>
  import("../pages/Admin/Setup/ManageServiceType/ViewManageServiceType")
);
const TPLPartner = lazy(() => import("../pages/Admin/Setup/TPLPartner"));
const ViewTPLPartner = lazy(() =>
  import("../pages/Admin/Setup/TPLPartner/ViewTPLPartner")
);
const AmazonOrder = lazy(() => import("../pages/Admin/Shipment/AmazonOrder"));

// Admin - Setup pages
const HubList = lazy(() => import("../pages/Admin/Setup/Hubs"));
const RegionList = lazy(() => import("../pages/Admin/Setup/Regions"));
const CityList = lazy(() => import("../pages/Admin/Setup/Cities"));
const Department = lazy(() => import("../pages/Admin/Setup/Departments"));
const EmployeeList = lazy(() => import("../pages/Admin/Setup/Employees"));

// const AmazonRateList = lazy(() => import("../pages/Admin/Setup/Amazon/Rates"));
// const AmazonAddRate = lazy(() => import("../pages/Admin/Setup/Amazon/Rates/Add"));
const AmazonCharges = lazy(() => import("../pages/Admin/Setup/Amazon/Charges"));
// const AmazonUpdatePrice = lazy(() => import("../pages/Admin/Setup/Amazon/Prices/Update"));

// Order Charges
const ViewOrderCharges = lazy(() => import("../pages/Admin/Shipment/OrderCharges/View"));

const UpdateUserPermission = lazy(() =>
  import("../pages/Admin/Setup/UpdateUserPermission")
);
const ClientList = lazy(() => import("../pages/Admin/Setup/Clients"));
const ClientDetail = lazy(() =>
  import("../pages/Admin/Setup/Clients/components/ClientForm")
);
const ClientNotifyDetail = lazy(() =>
  import("../pages/Admin/Setup/Clients/components/ClientNotifyForm")
);
const ClientNotifyList = lazy(() => import("../pages/ClientNotifyList"));
const PickupRequest = lazy(() => import("../pages/PickupRequest"));
const CreateCustomRequest = lazy(() =>
  import("../pages/PickupRequest/PickupRequestForm")
);
const CustomNotifyList = lazy(() => import("../pages/CustomNotifyList"));

const FulfillmentReport = lazy(() =>
  import("../pages/Inbound/FulfillmentReport")
);

// Rates
const TPLRates = lazy(() => import("../pages/Admin/Setup/TPLPartner/Rates"));
const ClientRates = lazy(() => import("../pages/Admin/Setup/Clients/Rates"));
const AddClientRate = lazy(() => import("../pages/Admin/Setup/Clients/Rates/AddClientRate"));
const ViewClientRate = lazy(() => import("../pages/Admin/Setup/Clients/Rates/ViewClientRate"));
const AddTPLRate = lazy(() => import("../pages/Admin/Setup/TPLPartner/Rates/AddRate"));
const ViewTPLRate = lazy(() => import("../pages/Admin/Setup/TPLPartner/Rates/ViewRate"));

const CityTiers = lazy(() => import("../pages/Admin/Setup/CityTiers"));
const CityZones = lazy(() => import("../pages/Admin/Setup/CityZones"));
const AddCityZone = lazy(() => import("../pages/Admin/Setup/CityZones/AddZone"));

const LastMileDeliverySheetList = lazy(() =>
  import("../pages/LastMile/LastMileDeliverySheetList")
);

const ViewDeliverySheetList = lazy(() =>
  import("../pages/LastMile/LastMileDeliverySheetList/ViewDeliverySheetList")
);

const CreateRunSheet = lazy(() =>
  import("../pages/LastMile/LastMileDeliverySheetList/CreateRunSheet")
);
const ManageCustomNotification = lazy(() =>
  import("../pages/ManageCustomNotification")
);

const TPLInvoice = lazy(() => import("../pages/Reports/TPLInvoice"));
// const TPLInvoiceForm = lazy(() => import("../pages/Reports/TPLInvoice/Form"));
const CODNInvoiceReportOld = lazy(() => import("../pages/Reports/COD&InvoiceReport"));

const DailyShipmentSummary = lazy(() =>
  import("../pages/DailyShipmentSummary")
);
const SearchOrder = lazy(() => import("../pages/Search&TrackOrder"));

const TrackOrder = lazy(() =>
  import("../pages/Search&TrackOrder/Order-history")
);
const AllowMultiples = lazy(() => import("../pages/AllowMultiples"));
const DailyDeliveryReport = lazy(() =>
  import("../pages/Reports/DailyDeliveryReport")
);
const AddClientSLARules = lazy(() =>
  import("../pages/Admin/Setup/Clients/SLARules/AddSLARule")
);
const ClientSLARules = lazy(() =>
  import("../pages/Admin/Setup/Clients/SLARules")
);
const SLAPerformance = lazy(() =>
  import("../pages/Reports/SLAPerformance")
);
const ClientSLAPerformanceReport = lazy(() =>
  import("../pages/Reports/ClientSLAPerformance")
);
const SLAPerformanceReport = lazy(() =>
  import("../pages/Reports/SLAPerformanceReport")
);

const LMInventoryScan = lazy(() => import("../pages/LastMile/LMInventoryScan"));
const LMInventory = lazy(() => import("../pages/LastMile/LMInventory"));

const RTSRequestList = lazy(() => import("../pages/RTSRequest"));
const RTSRequestForm = lazy(() =>
  import("../pages/RTSRequest/components/RTSRequestForm")
);

const DTSRequestList = lazy(() => import("../pages/DTSRequest"));
const DTSRequestForm = lazy(() =>
  import("../pages/DTSRequest/components/DTSRequestForm")
);

const Manifest = lazy(() => import("../pages/Manifest"));
const ManifestForm = lazy(() => import("../pages/Manifest/ManifestForm"));
const CustomManifestReport = lazy(() =>
  import("pages/Manifest/ManifestReport")
);

// Client Invoices
const ClientInvoices = lazy(() => import("../pages/Invoices/Client"));
const CreateClientInvoice = lazy(() => import("../pages/Invoices/Client/Form"));

// TPL Invoices
const TPLInvoices = lazy(() => import("../pages/Invoices/TPL"));
const CreateTPLInvoice = lazy(() => import("../pages/Invoices/TPL/Form"));

const TPLManifestDraft = lazy(() => import("pages/TPLManifestDraft"));
const B2BMarkDelivered = lazy(() => import("../pages/B2BMarkDelivered"));
// const CargoShipments = lazy(() => import("../pages/Cargo/Shipments"));
// const CargoManifests = lazy(() => import("../pages/Cargo/Manifests"));
const CargoManifest = lazy(() => import("../pages/Cargo/Manifests/Form"));

// const AssignedToNBNLM = lazy(() => import("../pages/AssignedToNBNLM"));
const TplManifest = lazy(() => import("../pages/TPLManifest"));
const TplManifestForm = lazy(() =>
  import("../pages/TPLManifest/TPLManifestForm")
);
const TplManifestReport = lazy(() =>
  import("../pages/TPLManifest/TplManifestReport")
);
const RVPRunSheetList = lazy(() => import("../pages/RVPRunSheet"));
const ViewRVPSheet = lazy(() => import("../pages/RVPRunSheet/View"));
const CreateRVPRunSheet = lazy(() => import("../pages/RVPRunSheet/Create"));

const TPLHandOverScan = lazy(() => import("pages/Scan/TPLHandOverScan"));

const StockInOut = lazy(() => import("pages/Client/Warehouse/StockInOut"));
const CurrentStock = lazy(() => import("pages/Client/Warehouse/CurrentStock"));
const OldCurrentStock = lazy(() => import("pages/Client/Warehouse/OldCurrentStock"));
const NewWMSCurrentStock = lazy(() => import("pages/Client/Warehouse/Inventory"));
const StockInfo = lazy(() => import("pages/Client/Warehouse/StockInfo"));
const SupportTickets = lazy(() => import("pages/Client/SupportTickets"));

const TPLCityList = lazy(() => import("pages/Admin/Setup/TPLCities"));
const TPLPrintLabelLogs = lazy(() => import("pages/Admin/Logs/3pl-label"));
const PrintLabelLogs = lazy(() => import("pages/Admin/Logs/PrintLabel"));
const PrintInvoiceLogs = lazy(() => import("pages/Admin/Logs/PrintInvoice"));
const ExcelExportLogs = lazy(() => import("pages/Admin/Logs/ExcelExport"));
const TPLStatusLogs = lazy(() => import("pages/Admin/Logs/3pl-status"));
const ClientStatusLogs = lazy(() => import("pages/Admin/Logs/Client"));
const NotificationLogs = lazy(() => import("pages/Admin/Logs/Notification"));
const InboundStatusLogs = lazy(() => import("pages/Admin/Logs/InboundStatus"));
const AmazonWeeklyReportLogs = lazy(() => import("pages/Admin/Logs/AmazonReport"));
const ClientExcelExportLogs = lazy(() => import("pages/Client/Logs/ExcelExport"));
const ClientPrintLabelLogs = lazy(() => import("pages/Client/Logs/PrintLabel"));
const StatusSyncLogs = lazy(() => import("pages/Admin/Logs/StatusSync"));
const MissingShortAddressLogs = lazy(() => import("pages/Admin/Logs/BadAddress"));
const MissingShortAddressLogsClient = lazy(() => import("pages/Client/Logs/BadAddress"));

const HandedOverToCourier = lazy(() =>
  import("pages/Admin/Shipment/HandedOverToCourierList")
);

const BulkOperations = lazy(() => import("pages/Admin/Shipment/BulkOperations"));
const UpdatePODOperation = lazy(() => import("pages/Admin/Shipment/UpdatePODOperation"));
const PaymentsPage = lazy(() => import("pages/Payment"));
const CreatePayment = lazy(() => import("pages/Payment/createPayment"));
const MerchalinkPayment = lazy(() => import("pages/Payment/merchalinkPayment"));
// const WMSSetup = lazy(() => import("pages/Admin/Setup/WMSSetup"));

const PaymentGateway = lazy(() => import("pages/PaymentGateway"));
const PaymentConfirmation = lazy(() =>
  import("pages/PaymentGateway/confirmationPage")
);

const PaymentError = lazy(() => import("pages/PaymentGateway/PayementError"));
const PublicTrackOrder = lazy(() => import("pages/Public/TrackOrder"));
const FAQ = lazy(() => import("pages/Public/FAQ"));
const PrivacyPolicy = lazy(() => import("pages/Public/PrivacyPolicy"));

// Failed orders
const FailedOrders = lazy(() => import("../pages/FailedOrders"));

const Routes = () => {
  const location = useLocation();

  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.99
    },
    in: {
      opacity: 1,
      scale: 1
    },
    out: {
      opacity: 0,
      scale: 1.01
    }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4
  };

  return (
    <AnimatePresence>
      <Suspense fallback={<SuspenseLoading />}>
        <Switch>
          <Redirect exact from="/" to="/login" />
          <Route
            path={[
              // Admin Pages
              "/admin/dashboard",
              "/admin/report/daily-pickup/client-wise",
              "/admin/user-permission",
              "/admin/logs/print-3pl",
              "/admin/logs/print-label",
              "/admin/logs/print-invoice",
              "/admin/logs/excel-export",
              "/admin/logs/3pl-status",
              "/admin/logs/client",
              // "/admin/setup/wms",
              "/admin/logs/notification",
              "/admin/logs/inbound-status",
              "/admin/logs/amazon/weekly-report",
              // Amazon
              "/setup/amazon/charges",
              "/setup/amazon/rates",
              "/setup/amazon/add-rates",
              
              // Invoice
              "/client-invoices",
              "/client-invoice",
              "/client-invoice/:id",
              
              // Rates
              "/setup/clients/rates",
              "/setup/clients/add-rate",
              "/setup/clients/view-rates/:id",
              "/setup/tpl/rates",
              "/setup/tpl/rates",
              "/setup/tpl/add-rate",
              "/setup/tpl/view-rates/:id",

              // SLA rules
              "/setup/clients/sla-rules",
              "/setup/clients/add-sla-rules",

              // City Tiers/Zones
              "/setup/city-tiers",
              "/setup/city-zones",
              "/setup/add-city-zone",
              "/hubs",
              "/regions",
              "/cities",
              "/departments",
              "/employees",
              "/clients",
              "/logs/excel-export",
              "/logs/print-label",
              "/admin/shipments",
              "/admin/print-shipments",
              "/pickup-request",
              "/clients-notify-request",
              "/create-notify-request",
              "/custom-request-list",
              "/create-costom-request",
              "/custom-notify-list",
              "/client/:clientId",
              // Client Pages
              "/dashboard",
              "/clientshipinglist",
              "/uploadshipments",
              "/single-shipment-upload",
              "/bulkshipmentupload",
              "/pickup-requests",
              // Old WMS
              "/inventory/inbound",
              "/inventory/inbounds",
              "/inventory/product-sku/list",
              "/inventory/inbound-upload/bulk",
              "/inventory/product-sku/bulk-upload",
              // Order Charge
              "/orders/:id/charges",
              // New WMS
              "/inventory/product-sku/upload",
              "/support-tickets",
              "/order",
              "/amazon/order",
              "/manageservicetype",
              "/manageservicetype-detail",
              "/tpl-partner",
              "/tpl-partner-details",
              "/print-label",
              "/fulfillment-report",
              "/custom-notify-custom-list",
              "/custom-notify-details",
              // "/bag-listing",
              // "/bag",
              // "/edit-bag",
              // "/trip-sheet",
              // "/trip-sheet-listing",
              "/pickupscan",
              "/inscan",
              "/outscan",
              "/rthscan",
              "/scan/pickup/reject",
              "/scan/pickup/cancelled",
              "/scan/departed-from-airport",
              "/scan/arrived-at-airport",
              "/scan/flight-delayed",
              "/scan/arranged-for-delivery",
              "/scan/delivery-cancelled",
              "/scan/delivery-rejected",
              "/cargo/in-scan-origin",
              "/cargo/out-scan-origin",
              "/cargo/in-scan-dest",
              "/customsshipinglist",
              "/first-mile-pickup-list",
              "/first-mile-view-pickup-request",
              "/firstmileshipinglist",
              "/last-mile-delivery-list",
              "/last-mile-delivery-sheet",
              "/create-run-sheet",
              "/inboundshipinglist",
              "/outboundshipinglist",
              "/lastmileshipinglist",
              "/tpl-invoice/:id",
              "/tpl-invoice",
              "/tpl-invoices",
              "/cod-report",
              "/invoice-report",
              "/daily-shipment-summary",
              "/search",
              "/track",
              "/delivered",
              "/undelivered",
              "/outfordelivery",
              "/returntoshipper",
              "/rthinscan",
              "/rthoutscan",
              "/rtohubscan",
              "/rtsscan",
              "/tpl-remitted",
              "/tpl-not-remitted",
              "/lminventoryscan",
              "/lm-inventory",
              "/allow-multiples",
              "/daily-delivery-report",
              "/sla-performance",
              "/reports/sla-performance",
              "/reports/client-sla-performance",
              "/manage-rts-requests",
              "/rts-request",
              "/manage-dts-requests",
              "/dts-request",
              "/manifests",
              "/manifest",
              "/manifest-report",
              // "/cargo-shipments",
              // "/cargo-manifests",
              "/cargo-manifest",
              "/b2b-mark-as-delivered",
              "/rvp-orders",
              // "/assigned-to-nbn-lm",
              "/manage-tpl-manifest",
              "/tpl-manifest",
              "/rvp-in-scan",
              "/rvp-out-scan",
              "/rvp-run-sheet-list",
              "/create-rvp-run-sheet",
              "/rvp-run-sheet",
              "/tpl-manifest-report",
              
              "/tpl-manifest-draft",
              "/shipments",
              "/tpl-handover-scan",
              "/stock-in-out",
              "/stock-info",
              "/current-stock",
              "/old-current-stock",
              "/wms/current-stock",
              "/admin/tpl-cities",
              // "/admin/scan-undelivery",
              "/handed-over-to-courier",
              "/admin/bulk-operations",
              "/admin/update-pod",
              "/admin/logs/failed-orders",
              "/admin/logs/status-sync",
              "/admin/logs/bad-address",
              "/logs/bad-address",

              // "/payment",
              // "/payment/merchalink",
              // "/payment-history"
            ]}>
            <LeftSidebar>
              <Switch location={location} key={location.pathname}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}>
                  {/* Admin Dashboards */}
                  <PrivateRoute path="/dashboard" component={Dashboard} />
                  <PrivateRoute path="/admin/dashboard" component={AdminDashboard} />
                  <PrivateRoute path="/a4-dashboard" component={A4Dashboard} />
                  <PrivateRoute
                    path="/admin/report/daily-pickup/client-wise"
                    component={ClientWiseDailyPickupReport}
                  />

                  {/* Setup pages */}

                  <PrivateRoute
                    path="/admin/user-permission/:id"
                    component={UpdateUserPermission}
                  />
                  <PrivateRoute path="/hubs" component={HubList} />
                  <PrivateRoute path="/regions" component={RegionList} />

                  {/* Rates */}
                  <PrivateRoute path="/setup/clients/rates" component={ClientRates} />
                  <PrivateRoute path="/setup/clients/add-rate" component={AddClientRate} />
                  <PrivateRoute path="/setup/clients/view-rates/:id" component={ViewClientRate} />

                  <PrivateRoute path="/setup/tpl/rates" component={TPLRates} />
                  <PrivateRoute path="/setup/tpl/add-rate" component={AddTPLRate} />
                  <PrivateRoute path="/setup/tpl/view-rates/:id" component={ViewTPLRate} />

                  {/* City setup */}
                  <PrivateRoute path="/setup/city-tiers" component={CityTiers} />
                  <PrivateRoute path="/setup/city-zones" component={CityZones} />
                  <PrivateRoute path="/setup/add-city-zone" component={AddCityZone} />

                  {/* Amazon */}
                  <PrivateRoute path="/setup/amazon/charges" component={AmazonCharges} />
                  <PrivateRoute path="/setup/clients/sla-rules" component={ClientSLARules} />
                  <PrivateRoute path="/setup/clients/add-sla-rules" component={AddClientSLARules} />
                  <PrivateRoute path="/cities" component={CityList} />
                  <PrivateRoute path="/departments" component={Department} />
                  <PrivateRoute path="/employees" component={EmployeeList} />
                  <PrivateRoute path="/clients" component={ClientList} />
                  <PrivateRoute path="/admin/shipments" component={AdminShipmentList} />
                  <PrivateRoute path="/admin/print-shipments" component={AdminPrintShipmentList} />
                  {/* Order Charge */}
                  <PrivateRoute path="/orders/:id/charges" component={ViewOrderCharges} />
                  <PrivateRoute
                    path="/client/:clientId"
                    component={ClientDetail}
                  />
                  <PrivateRoute
                    path="/pickup-requests"
                    component={PickupRequest}
                  />
                  <PrivateRoute
                    path="/pickup-request"
                    component={CreateCustomRequest}
                  />
                  <PrivateRoute
                    path="/clients-notify-request"
                    component={ClientNotifyList}
                  />
                  <PrivateRoute
                    path="/create-notify-request"
                    component={ClientNotifyDetail}
                  />
                  <PrivateRoute
                    path="/custom-request-list"
                    component={PickupRequest}
                  />
                  {/* TODO: check this route is required or not */}
                  <PrivateRoute
                    path="/custom-notify-list"
                    component={CustomNotifyList}
                  />
                  <PrivateRoute
                    path="/custom-notify-custom-list"
                    component={ManageCustomNotification}
                  />
                  <PrivateRoute
                    path="/custom-notify-details"
                    component={ViewManageCustomNotification}
                  />
                  <PrivateRoute
                    path="/create-costom-request"
                    component={CreateCustomRequest}
                  />
                  <PrivateRoute
                    path="/order/:id/:action?"
                    component={SingleShipmentUpload}
                  />
                  <PrivateRoute
                    path="/amazon/order/:id"
                    component={AmazonOrder}
                  />
                  <PrivateRoute
                    path="/manageservicetype"
                    component={ManageServiceType}
                  />
                  <PrivateRoute
                    path="/manageservicetype-detail/:id"
                    component={ViewManageServiceType}
                  />
                  <PrivateRoute path="/tpl-partner" component={TPLPartner} />
                  <PrivateRoute path="/pickupscan" component={PickupScan} />
                  <PrivateRoute path="/inscan" component={InScan} />
                  <PrivateRoute path="/outscan" component={OutScan} />
                  <PrivateRoute path="/rthscan" component={RTHScan} />
                  <PrivateRoute path="/cargo/in-scan-origin" component={CargoInScan} />
                  <PrivateRoute path="/cargo/out-scan-origin" component={CargoOutScan} />
                  <PrivateRoute path="/cargo/in-scan-dest" component={CargoDestInScan} />
                  <PrivateRoute path="/scan/pickup/reject" component={PickupRejectScan} />
                  <PrivateRoute path="/scan/pickup/cancelled" component={PickupCancelledScan} />
                  <PrivateRoute path="/scan/arranged-for-delivery" component={ArrangedForDelivery} />
                  <PrivateRoute path="/scan/arrived-at-airport" component={ArrivedAtAirportScan} />
                  <PrivateRoute path="/scan/departed-from-airport" component={DepartedFromAirport} />
                  <PrivateRoute path="/scan/flight-delayed" component={FlightDelayed} />
                  <PrivateRoute path="/scan/delivery-cancelled" component={DeliveryCancelled} />
                  <PrivateRoute path="/scan/delivery-rejected" component={DeliveryRejected} />


                  <PrivateRoute
                    path="/tpl-partner-details"
                    component={ViewTPLPartner}
                  />
                  <PrivateRoute
                    path="/fulfillment-report"
                    component={FulfillmentReport}
                  />
                  {/* <PrivateRoute path="/bag-listing" component={Bags} />
                  <PrivateRoute path="/bag" component={Bag} />
                  <PrivateRoute path="/edit-bag" component={EditBag} /> */}
                  {/* <PrivateRoute
                    path="/trip-sheet-listing"
                    component={TripSheets}
                  />
                  <PrivateRoute path="/trip-sheet" component={TripSheet} /> */}
                  <PrivateRoute
                    path="/customsshipinglist"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute
                    path="/firstmileshipinglist"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute
                    path="/last-mile-delivery-list"
                    component={LastMileDeliverySheetList}
                  />
                  {/* TODO: chech this is used, if not remove this & respective files */}
                  <PrivateRoute
                    path="/last-mile-delivery-sheet"
                    component={ViewDeliverySheetList}
                  />
                  <PrivateRoute
                    path="/create-run-sheet"
                    component={CreateRunSheet}
                  />

                  <PrivateRoute
                    path="/inboundshipinglist"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute
                    path="/outboundshipinglist"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute
                    path="/lastmileshipinglist"
                    component={AdminShipmentList}
                  />

                    {/* Invoices */}
                    <PrivateRoute exact path="/client-invoice" component={CreateClientInvoice} />
                    <PrivateRoute exact path="/client-invoice/:id" component={CreateClientInvoice} />
                    <PrivateRoute path="/client-invoices" component={ClientInvoices} />

                    <PrivateRoute exact path="/tpl-invoice" component={CreateTPLInvoice} />
                    <PrivateRoute exact path="/tpl-invoice/:id" component={CreateTPLInvoice} />
                    <PrivateRoute path="/tpl-invoices" component={TPLInvoices} />
                  <PrivateRoute path="/cod-report" component={CODNInvoiceReportOld} />
                  <PrivateRoute
                    path="/invoice-report"
                    component={TPLInvoice}
                  />
                  <PrivateRoute
                    path="/daily-shipment-summary"
                    component={DailyShipmentSummary}
                  />
                  <PrivateRoute path="/search" component={SearchOrder} />
                  <PrivateRoute
                    path="/delivered"
                    component={DeliveredOrderList}
                  />
                  <PrivateRoute
                    path="/undelivered"
                    component={UnDeliveredOrderList}
                  />
                  <PrivateRoute
                    path="/outfordelivery"
                    component={OutForDelivery}
                  />
                  <PrivateRoute
                    path="/returntoshipper"
                    component={RTSOrderList}
                  />
                  <PrivateRoute path="/track/:awbNo" component={TrackOrder} />
                  <PrivateRoute path="/rthinscan" component={RTHInScan} />
                  <PrivateRoute path="/rthoutscan" component={RTHOutScan} />
                  <PrivateRoute path="/rtohubscan" component={RTOHubScan} />
                  <PrivateRoute path="/rtsscan" component={RTSScan} />
                  <PrivateRoute
                    path="/allow-multiples"
                    component={AllowMultiples}
                  />

                  <PrivateRoute
                    path="/tpl-remitted"
                    component={TPLRemittedOrderList}
                  />
                  <PrivateRoute
                    path="/tpl-not-remitted"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute
                    path="/lminventoryscan"
                    component={LMInventoryScan}
                  />
                  <PrivateRoute path="/lm-inventory" component={LMInventory} />
                  <PrivateRoute
                    path="/daily-delivery-report"
                    component={DailyDeliveryReport}
                  />
                  <PrivateRoute
                    path="/sla-performance"
                    component={SLAPerformance}
                  />
                  <PrivateRoute
                    path="/reports/sla-performance"
                    component={SLAPerformanceReport}
                  />
                  <PrivateRoute
                    path="/reports/client-sla-performance"
                    component={ClientSLAPerformanceReport}
                  />

                  <PrivateRoute
                    path="/manage-rts-requests"
                    component={RTSRequestList}
                  />
                  <PrivateRoute
                    path="/rts-request"
                    component={RTSRequestForm}
                  />
                  <PrivateRoute
                    path="/manage-dts-requests"
                    component={DTSRequestList}
                  />
                  <PrivateRoute
                    path="/dts-request"
                    component={DTSRequestForm}
                  />
                  <PrivateRoute path="/manifests" component={Manifest} />
                  <PrivateRoute path="/manifest" component={ManifestForm} />
                  {/* <PrivateRoute
                    path="/cargo-shipments"
                    component={CargoShipments}
                  /> */}
                  {/* <PrivateRoute path="/cargo-manifests" component={CargoManifests} /> */}
                  <PrivateRoute path="/cargo-manifest" component={CargoManifest} />
                  <PrivateRoute
                    path="/b2b-mark-as-delivered"
                    component={B2BMarkDelivered}
                  />
                  <PrivateRoute path="/rvp-orders" component={RVPOrderList} />
                  {/* <PrivateRoute
                    path="/assigned-to-nbn-lm"
                    component={AssignedToNBNLM}
                  /> */}
                  <PrivateRoute
                    path="/manage-tpl-manifest"
                    component={TplManifest}
                  />
                  <PrivateRoute
                    path="/tpl-manifest"
                    component={TplManifestForm}
                  />
                  <PrivateRoute
                    path="/rvp-run-sheet-list"
                    component={RVPRunSheetList}
                  />
                  <PrivateRoute
                    path="/create-rvp-run-sheet"
                    component={CreateRVPRunSheet}
                  />
                  <PrivateRoute
                    path="/rvp-run-sheet"
                    component={ViewRVPSheet}
                  />
                  <PrivateRoute
                    path="/tpl-manifest-report"
                    component={TplManifestReport}
                  />
                  <PrivateRoute
                    path="/shipments"
                    component={AdminShipmentList}
                  />
                  <PrivateRoute path="/rvp-in-scan" component={RVPInScan} />
                  <PrivateRoute path="/rvp-out-scan" component={RVPOutScan} />
                  <PrivateRoute
                    path="/tpl-manifest-draft"
                    component={TPLManifestDraft}
                  />
                  <PrivateRoute
                    path="/tpl-handover-scan"
                    component={TPLHandOverScan}
                  />

                  <PrivateRoute
                    path="/manifest-report"
                    component={CustomManifestReport}
                  />
                  <PrivateRoute
                    path="/stock-in-out"
                    component={StockInOut}
                  />
                  <PrivateRoute
                    path="/current-stock"
                    component={CurrentStock}
                  />
                  <PrivateRoute
                    path="/old-current-stock"
                    component={OldCurrentStock}
                  />
                  <PrivateRoute
                    path="/wms/current-stock"
                    component={NewWMSCurrentStock}
                  />
                  <PrivateRoute
                    path="/stock-info"
                    component={StockInfo}
                  />
                  <PrivateRoute
                    path="/admin/tpl-cities"
                    component={TPLCityList}
                  />
                  <PrivateRoute
                    path="/admin/logs/print-3pl"
                    component={TPLPrintLabelLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/print-label"
                    component={PrintLabelLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/print-invoice"
                    component={PrintInvoiceLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/excel-export"
                    component={ExcelExportLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/3pl-status/:tplId?"
                    component={TPLStatusLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/client"
                    component={ClientStatusLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/notification"
                    component={NotificationLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/inbound-status"
                    component={InboundStatusLogs}
                  />
                  <PrivateRoute
                    path="/admin/logs/amazon/weekly-report"
                    component={AmazonWeeklyReportLogs}
                  />
                  {/* <PrivateRoute
                    path="/admin/scan-undelivery"
                    component={ScanUndelivery}
                  /> */}
                  <PrivateRoute
                    path="/logs/excel-export"
                    component={ClientExcelExportLogs}
                  />
                  <PrivateRoute
                    path="/logs/bad-address"
                    component={MissingShortAddressLogsClient}
                  />
                  <PrivateRoute
                    path="/logs/print-label"
                    component={ClientPrintLabelLogs}
                  />
                  <PrivateRoute
                    path="/handed-over-to-courier"
                    component={HandedOverToCourier}
                  />
                  <PrivateRoute
                    path="/admin/bulk-operations"
                    component={BulkOperations}
                  />
                  <PrivateRoute
                    path="/admin/update-pod"
                    component={UpdatePODOperation}
                  />
                  <PrivateRoute
                    path="/admin/logs/failed-orders"
                    component={FailedOrders}
                  />

                  <PrivateRoute
                    path="/admin/logs/status-sync"
                    component={StatusSyncLogs}
                  />

                  <PrivateRoute
                    path="/admin/logs/bad-address"
                    component={MissingShortAddressLogs}
                  />
                  {/* <PrivateRoute path="/admin/setup/wms" component={WMSSetup} /> */}
                  <PrivateRoute path="/payment" exact component={CreatePayment} />
                  <PrivateRoute path="/payment/merchalink" component={MerchalinkPayment} />
                  <PrivateRoute
                    path="/payment-history"
                    component={PaymentsPage}
                  />
                  {/* Client Pages */}
                  <PrivateRoute path="/clientshipinglist" component={ClientShipingList} />
                  <PrivateRoute path="/uploadshipments" component={ShipmentUpload} />
                  <PrivateRoute path="/single-shipment-upload" component={SingleShipmentUpload} />
                  <PrivateRoute path="/bulkshipmentupload" component={BulkShipmentUpload} />
                  <PrivateRoute path="/inventory/inbound/:id" component={ViewInbound} />
                  <PrivateRoute path="/inventory/inbounds" component={InboundList} />
                  <PrivateRoute path="/inventory/inbound-upload/bulk" component={InboundUpload} />
                  <PrivateRoute path="/inventory/product-sku/list" component={ProductSKUList} />
                  <PrivateRoute path="/inventory/product-sku/bulk-upload" component={ProductSKUUpload} />
                  <PrivateRoute path="/inventory/product-sku/upload" component={AddProductSKU} />
                  <PrivateRoute path="/support-tickets" component={SupportTickets} />
                  {/* <Route path="*" component={PageError404} /> */}
                </motion.div>
              </Switch>
            </LeftSidebar>
          </Route>

          <Route
            path={[
              "/",
              "/login",
              "/register-client",
              "/make-payment/:referenceId",
              "/payment-confirmation",
              "/payment-failure",
              "/faq",
              "/privacy-policy"
            ]}>
            <MinimalLayout>
              <Switch location={location} key={location.pathname}>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}>
                  <Route exact path="/" component={LandingPage} />
                  <Route path="/login" component={LoginPage} />
                  <Route path="/register-client" component={RegisterClient} />
                  <Route
                    path="/make-payment/:referenceId"
                    component={PaymentGateway}
                  />
                  <Route
                    path="/payment-confirmation"
                    component={PaymentConfirmation}
                  />
                  <Route
                    path="/payment-failure"
                    component={PaymentError}
                  />
                  <Route path="/public/track/:awbNo" component={PublicTrackOrder} />
                  <Route path="/faq" component={FAQ} />
                  <Route path="/privacy-policy" component={PrivacyPolicy} />
                </motion.div>
              </Switch>
            </MinimalLayout>
          </Route>
        </Switch>
      </Suspense>
    </AnimatePresence>
  );
};

export default Routes;
